import {
  GetClientMarketplaceFees,
  GetLocationMarketplaceConfigByLocationId,
  GetLocationMarketplaceConfigs,
  GetLocationMarketplaceStaff,
  GetLocationMarketplaceTerms,
  PostLocationMarketplaceTermsAcceptance,
  PutLocationMarketplaceAddressByLocationId,
  PutLocationMarketplaceConfigByLocationId,
  PutLocationMarketplaceDetailsByLocationId,
  PutLocationMarketplaceDescriptionByLocationId,
  PutLocationMarketplaceStaff,
  PutMarketplaceStaffSetting,
} from "@/server-types";
import { useCustomQuery } from "../custom-query";
import { get, post, put } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { queryClient } from "..";

/**
 * GET /location/marketplace
 */
export const useGetLocationMarketplaceConfigs = (isEnabled: boolean = true) => {
  return useCustomQuery({
    queryKey: ["locationMarketplaceConfigs"],
    queryFn: () => get<GetLocationMarketplaceConfigs>("/location/marketplace"),
    enabled: isEnabled,
  });
};

/**
 * GET /location/marketplace/terms
 */
export const useGetLocationMarketplaceTerms = () => {
  return useCustomQuery({
    queryKey: ["locationMarketplaceTerms"],
    queryFn: () =>
      get<GetLocationMarketplaceTerms>("/location/marketplace/terms"),
  });
};

/**
 * POST /location/marketplace/terms/acceptance
 */
export const usePostLocationMarketplaceTermsAcceptance = () => {
  return useCustomMutation({
    mutationFn: (body: PostLocationMarketplaceTermsAcceptance["body"]) =>
      post<PostLocationMarketplaceTermsAcceptance>(
        "/location/marketplace/terms/acceptance",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["locationMarketplaceTerms"],
      });
    },
  });
};

/**
 * GET /location/marketplace/:locationId
 */
export const useGetLocationMarketplaceConfigByLocationId = (
  locationId: number,
  isEnabled: boolean = true,
) => {
  return useCustomQuery({
    queryKey: ["locationMarketplaceConfig", locationId],
    queryFn: () =>
      get<GetLocationMarketplaceConfigByLocationId>(
        `/location/marketplace/${locationId}`,
      ),
    enabled: isEnabled,
  });
};

/**
 * PUT /location/marketplace/:locationId/config
 */
export const usePutLocationMarketplaceConfigByLocationId = () => {
  return useCustomMutation({
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: number;
      body: PutLocationMarketplaceConfigByLocationId["body"];
    }) =>
      put<PutLocationMarketplaceConfigByLocationId>(
        `/location/marketplace/${locationId}/config`,
        body,
      ),
    // Returned so mutateAsync resolves only once the refetch lands; the setup frame navigates on it.
    onSuccess: (_response, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["locationMarketplaceConfig", variables.locationId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["locationMarketplaceConfigs"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["locations"],
        }),
      ]),
  });
};

/**
 * PUT /location/marketplace/:locationId/address
 */
export const usePutLocationMarketplaceAddressByLocationId = () => {
  return useCustomMutation({
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: number;
      body: PutLocationMarketplaceAddressByLocationId["body"];
    }) =>
      put<PutLocationMarketplaceAddressByLocationId>(
        `/location/marketplace/${locationId}/address`,
        body,
      ),
    // Returned so mutateAsync resolves only once the refetch lands; the setup frame navigates on it.
    onSuccess: (_response, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["locationMarketplaceConfig", variables.locationId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["locationMarketplaceConfigs"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["locations"],
        }),
      ]),
  });
};

/**
 * PUT /location/marketplace/:locationId/description
 */
export const usePutLocationMarketplaceDescriptionByLocationId = () => {
  return useCustomMutation({
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: number;
      body: PutLocationMarketplaceDescriptionByLocationId["body"];
    }) =>
      put<PutLocationMarketplaceDescriptionByLocationId>(
        `/location/marketplace/${locationId}/description`,
        body,
      ),
    // Returned so mutateAsync resolves only once the refetch lands; the setup frame navigates on it.
    onSuccess: (_response, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["locationMarketplaceConfig", variables.locationId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["locationMarketplaceConfigs"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["locations"],
        }),
      ]),
  });
};

/**
 * PUT /location/marketplace/:locationId/details
 */
export const usePutLocationMarketplaceDetailsByLocationId = () => {
  return useCustomMutation({
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: number;
      body: PutLocationMarketplaceDetailsByLocationId["body"];
    }) =>
      put<PutLocationMarketplaceDetailsByLocationId>(
        `/location/marketplace/${locationId}/details`,
        body,
      ),
    // Returned so mutateAsync resolves only once the refetch lands; the setup frame navigates on it.
    onSuccess: (_response, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["locationMarketplaceConfig", variables.locationId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["locationMarketplaceConfigs"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["locations"],
        }),
      ]),
  });
};

/**
 * GET /client/marketplace-fee
 */
export const useGetMarketplaceFees = (isEnabled: boolean = true) => {
  return useCustomQuery({
    queryKey: ["marketplaceFees"],
    queryFn: () => get<GetClientMarketplaceFees>("/client/marketplace-fee"),
    enabled: isEnabled,
  });
};

/**
 * GET /location/marketplace/:locationId/staff
 */
export const useGetLocationMarketplaceStaff = (
  locationId: number,
  isEnabled: boolean = true,
) => {
  return useCustomQuery({
    queryKey: ["locationMarketplaceStaff", locationId],
    queryFn: () =>
      get<GetLocationMarketplaceStaff>(
        `/location/marketplace/${locationId}/staff`,
      ),
    enabled: isEnabled,
  });
};

/**
 * PUT /location/marketplace/:locationId/staff
 */
export const usePutLocationMarketplaceStaff = () => {
  return useCustomMutation({
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: number;
      body: PutLocationMarketplaceStaff["body"];
    }) =>
      put<PutLocationMarketplaceStaff>(
        `/location/marketplace/${locationId}/staff`,
        body,
      ),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["locationMarketplaceStaff", variables.locationId],
      });
    },
  });
};

/**
 * PUT /location/marketplace/staff
 */
export const usePutMarketplaceStaffSetting = () => {
  return useCustomMutation({
    mutationFn: (body: PutMarketplaceStaffSetting["body"]) =>
      put<PutMarketplaceStaffSetting>("/location/marketplace/staff", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["locationMarketplaceStaff"],
      });
    },
  });
};
